import moment from 'moment';
import { AxiosResponse } from 'axios';
import { runInAction } from 'mobx';

import { notUndefined } from 'ui/base/types';

import { Optional, ResultKind } from 'services/base/types';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';

import { HostsService } from 'services/pangolin/hosts_service';

import {
  VPNServerArmadilloGetResponse,
  VPNServerArmadilloListResponse,
  VPNServerArmadilloCreateResponse,
  VPNServerArmadilloObject,
  VPNServerArmadilloSetRequest,
} from 'services/accounts/protos/vpnserver_armadillo_pb';

import {
  VPNServerAppStatus,
} from 'services/accounts/protos/vpnserver_pb';

import {
  Host,
  IPangolinDomain,
  VPNClient,
  VPNClientMap,
} from './types';

type PartialVPNClientWithID = Pick<VPNClient, 'id'> & Partial<VPNClient>;

export type VPNClientUpdateResult =
  | { kind: ResultKind.OK; client: PartialVPNClientWithID; }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number;
      nameError?: string; };

export type VPNClientDeleteResult =
  | { kind: ResultKind.OK; }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number; };

export class VPNClientService {
  static async fetch(domain: IPangolinDomain, id: string, save = true): Promise<Optional<VPNClient>> {
    const httpResp = await http.get(`/pangolin/${domain.id}/vpnserver/clients/${id}`);
    return runInAction(() => {
      const remoteClient = parseVPNClientResponse(httpResp);
      if (remoteClient == null) return undefined;

      if (save) {
        const localClient = domain.vpnClients.get(id);
        if (localClient == null) {
          domain.vpnClients.set(remoteClient.id, remoteClient);
          domain.vpnClients.sort(clientSorter);
        } else {
          Object.assign(localClient, remoteClient);
        }
      }

      return remoteClient;
    });
  }

  static async fetchAll(domain: IPangolinDomain, save = true): Promise<VPNClientMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/vpnserver/clients`);
    const remoteClients = parseVPNClientsResponse(httpResp);
    remoteClients.sort(clientSorter);
    return runInAction(() => {
      if (save) domain.vpnClients = remoteClients;
      return remoteClients;
    });
  }

  static async create(domain: IPangolinDomain, client: Partial<VPNClient>): Promise<VPNClientUpdateResult> {
    const request = createUpdateVPNClientsRequest(client);
    try {
      const httpResp = await http.post(`/pangolin/${domain.id}/vpnserver/clients`, createRequestString(request));
      const remoteClient = parseVPNClientCreateResponse(httpResp);
      return runInAction(() => {
        if (remoteClient != null) {
          domain.vpnClients.set(remoteClient.id, remoteClient);
          domain.vpnClients.sort(clientSorter);
          const hostId = remoteClient.hostId;
          if (hostId) HostsService.fetch(domain, hostId);
          return { kind: ResultKind.OK, client: remoteClient };
        }

        return { kind: ResultKind.USER_ERROR, status: 404 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async update(domain: IPangolinDomain, client: PartialVPNClientWithID): Promise<VPNClientUpdateResult> {
    const request = createUpdateVPNClientsRequest(client);
    try {
      await http.put(`/pangolin/${domain.id}/vpnserver/clients/${client.id}`, createRequestString(request));
      const remoteClient = await this.fetch(domain, client.id, false);
      return runInAction(() => {
        if (remoteClient != null) {
          const remotePartial = updatePartialFromComplete(client, remoteClient);
          updatePersistentFromPartial(domain, remotePartial);
          const hostId = remotePartial.hostId;
          if (hostId) HostsService.fetch(domain, hostId);
          return { kind: ResultKind.OK, client: remotePartial };
        }

        return { kind: ResultKind.USER_ERROR, status: 404 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async delete(domain: IPangolinDomain, client: VPNClient): Promise<VPNClientDeleteResult> {
    try {
      await http.delete(`/pangolin/${domain.id}/vpnserver/clients/${client.id}`);
      return runInAction(() => {
        domain.vpnClients.delete(client.id);
        return { kind: ResultKind.OK };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function parseVPNClientResponse(httpResp: AxiosResponse): Optional<VPNClient> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = VPNServerArmadilloGetResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getClient();
  return clientFromProto(itemProto);
}

function parseVPNClientsResponse(httpResp: AxiosResponse): VPNClientMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = VPNServerArmadilloListResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getClientsList();
  const clientObjects = listProto.map(clientFromProto).filter(notUndefined);
  return new VPNClientMap(clientObjects.map((c) => [c.id, c]));
}

function parseVPNClientCreateResponse(httpResp: AxiosResponse): Optional<VPNClient> {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = VPNServerArmadilloCreateResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getClient();
  return clientFromProto(itemProto);
}

function updatePersistentFromPartial(domain: IPangolinDomain, partial: PartialVPNClientWithID): void {
  const target = domain.vpnClients.get(partial.id);
  if (target != null) Object.assign(target, partial);
}

function updatePartialFromComplete(partial: PartialVPNClientWithID, complete: VPNClient): PartialVPNClientWithID {
  const updated: PartialVPNClientWithID = Object.keys(partial).reduce((result, key) => {
    if (Object.hasOwn(complete, key)) {
      (result as Record<string, unknown>)[key] = (complete as Record<string, unknown>)[key];
    }
    return result;
  }, { id: partial.id });

  return updated;
}

function createUpdateVPNClientsRequest(client: Partial<VPNClient>): VPNServerArmadilloSetRequest {
  const request = new VPNServerArmadilloSetRequest();

  const proto = new VPNServerArmadilloObject();

  if (client.name != null) {
    proto.setName(client.name);
  }

  if (client.isEnabled != null) {
    proto.setIsEnabled(client.isEnabled);
  }

  request.setClient(proto);

  return request;
}

function clientFromProto(proto: VPNServerArmadilloObject): Optional<VPNClient> {
  const id = proto.getId();
  if (id == null) {
    return undefined;
  }

  return {
    id,
    name: proto.getName() ?? '',
    nonce: proto.getNonce() ?? '',
    mnemonic: proto.getMnemonic() ?? '',
    status: proto.getStatus() ?? VPNServerAppStatus.VPNSERVER_APP_STATUS_OFFLINE,
    isEnabled: proto.getIsEnabled() ?? false,
    publicIP: proto.getPublicIp(),
    lastSeen: createSeen(proto.getLastSeen()),
    hostId: proto.getHostId(),
  };
}

function createSeen(seen: string | undefined): Date | undefined {
  if (seen == null || seen.trim().length === 0) {
    return undefined;
  } else {
    return moment.utc(seen).toDate();
  }
}

function clientSorter(a: VPNClient, b: VPNClient): number {
  const aName = a.name.toUpperCase();
  const bName = b.name.toUpperCase();
  return aName.localeCompare(bName);
}

export function getVPNClientHost(domain: IPangolinDomain, client: VPNClient): Host | undefined {
  return client.hostId ? domain.hosts.get(client.hostId) : undefined;
}
